import React, { useEffect, useRef, useState } from "react";

const PLACEHOLDERS = [
  "{{lead_name}}",
  "{{activity_type}}",
  "{{next_contact_date}}",
  "{{notes}}",
  "{{rep_name}}",
  "{{link}}",
];

/* ============================================================
   Default template seeded on first use (C4)
   ============================================================ */
export const DEFAULT_TEMPLATE_SUBJECT =
  "Reminder: Next Contact for {{lead_name}}";

export const DEFAULT_TEMPLATE_BODY = `<p>Hi {{rep_name}},</p>
<p>You have a <strong>{{activity_type}}</strong> planned for <strong>{{lead_name}}</strong> on <strong>{{next_contact_date}}</strong>.</p>
<p>Notes: {{notes}}</p>
<p><a href="{{link}}">Open in CRM</a></p>
<p>Thanks,<br/>IVSG CRM</p>`;

/* ============================================================
   HTML sanitizer for paste (C2)
   Allows only the tags email clients accept. Strips styles,
   classes, on* handlers, and dangerous elements.
   ============================================================ */
const ALLOWED_TAGS = new Set([
  "P", "DIV", "BR", "SPAN",
  "STRONG", "B", "EM", "I", "U", "S", "SUB", "SUP",
  "A",
  "UL", "OL", "LI",
  "H1", "H2", "H3", "H4", "H5", "H6",
  "IMG",
  "TABLE", "THEAD", "TBODY", "TR", "TD", "TH",
  "BLOCKQUOTE", "HR",
]);

const ALLOWED_ATTRS_BY_TAG = {
  A: ["href", "target"],
  IMG: ["src", "alt", "width", "height"],
  TD: ["colspan", "rowspan"],
  TH: ["colspan", "rowspan"],
};

function sanitizeHtml(raw) {
  if (!raw) return "";
  try {
    const doc = new DOMParser().parseFromString(raw, "text/html");

    const walk = (node) => {
      const children = [...node.children];
      for (const child of children) {
        const tag = child.tagName;

        if (!ALLOWED_TAGS.has(tag)) {
          /* Replace the disallowed element with its text content */
          const text = document.createTextNode(child.textContent || "");
          child.replaceWith(text);
          continue;
        }

        /* Strip all attributes except the whitelisted ones for
           this tag. */
        const allowedForTag = ALLOWED_ATTRS_BY_TAG[tag] || [];
        for (const attr of [...child.attributes]) {
          if (!allowedForTag.includes(attr.name.toLowerCase())) {
            child.removeAttribute(attr.name);
          }
        }

        /* Link hardening */
        if (tag === "A") {
          if (!child.getAttribute("href")) child.remove();
          child.setAttribute("target", "_blank");
          child.setAttribute("rel", "noopener noreferrer");
        }

        /* Image validation */
        if (tag === "IMG") {
          const src = child.getAttribute("src") || "";
          if (!src) {
            child.remove();
            continue;
          }
          if (!/^(https?:|data:image\/)/i.test(src)) {
            child.remove();
            continue;
          }
        }

        walk(child);
      }
    };

    walk(doc.body);
    return doc.body.innerHTML;
  } catch {
    return String(raw).replace(/<[^>]*>/g, "");
  }
}

/* Detect data-URL images so we can warn the admin. */
function containsDataUrlImage(html) {
  if (!html) return false;
  return /<img[^>]+src=["']data:image\//i.test(html);
}

function EmailTemplateEditor({
  subject,
  body,
  onChange,
  disabled = false,
}) {
  const editorRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const [rawMode, setRawMode] = useState(false);
  const [rawText, setRawText] = useState(body || "");
  const [imgWarning, setImgWarning] = useState(false);

  /* --------------------------------------------------------
     Sync WYSIWYG with incoming body (only when parent changes it)
     -------------------------------------------------------- */
  useEffect(() => {
    if (rawMode) return;
    const el = editorRef.current;
    if (!el) return;
    if (el.innerHTML !== (body || "")) {
      el.innerHTML = body || "";
      setImgWarning(containsDataUrlImage(body || ""));
    }
  }, [body, rawMode]);

  useEffect(() => {
    if (rawMode) setRawText(body || "");
  }, [rawMode, body]);

  /* --------------------------------------------------------
     iOS keyboard handling — keep the editor visible when focused
     -------------------------------------------------------- */
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv || !editorRef.current) return;

    const onResize = () => {
      const el = editorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const keyboardHeight = window.innerHeight - vv.height;
      if (keyboardHeight > 100 && rect.bottom > vv.height) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    };

    vv.addEventListener("resize", onResize);
    return () => vv.removeEventListener("resize", onResize);
  }, []);

  /* --------------------------------------------------------
     Selection management for toolbar/placeholders
     -------------------------------------------------------- */
  const saveSelection = () => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (
      editorRef.current &&
      editorRef.current.contains(range.commonAncestorContainer)
    ) {
      savedSelectionRef.current = range.cloneRange();
    }
  };

  const restoreSelection = () => {
    const range = savedSelectionRef.current;
    if (!range) return false;
    const sel = window.getSelection();
    if (!sel) return false;
    sel.removeAllRanges();
    sel.addRange(range);
    return true;
  };

  const moveCaretToEnd = () => {
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    sel?.removeAllRanges();
    sel?.addRange(range);
  };

  const emitChange = () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    setImgWarning(containsDataUrlImage(html));
    onChange({ subject, body: html });
  };

  /* --------------------------------------------------------
     Toolbar commands
     -------------------------------------------------------- */
  const exec = (command, value = null) => {
    if (disabled) return;
    editorRef.current?.focus();
    if (!restoreSelection()) moveCaretToEnd();
    document.execCommand(command, false, value);
    saveSelection();
    emitChange();
  };

  const insertHtml = (html) => {
    if (disabled) return;
    editorRef.current?.focus();
    if (!restoreSelection()) moveCaretToEnd();
    document.execCommand("insertHTML", false, html);
    saveSelection();
    emitChange();
  };

  const insertPlaceholder = (token) => {
    if (disabled || rawMode) return;
    editorRef.current?.focus();
    if (!restoreSelection()) moveCaretToEnd();
    document.execCommand("insertText", false, token);
    saveSelection();
    emitChange();
  };

  const insertLink = () => {
    const url = window.prompt("Enter URL", "https://");
    if (!url) return;
    exec("createLink", url);
  };

  const insertImage = () => {
    const url = window.prompt(
      "Enter image URL (must be a hosted HTTPS URL for reliable email delivery)",
      "https://",
    );
    if (!url) return;
    insertHtml(
      `<img src="${url.replace(/"/g, "&quot;")}" alt="Logo" style="max-width:100%;height:auto;" />`,
    );
  };

  const removeFormat = () => {
    exec("removeFormat");
  };

  /* --------------------------------------------------------
     Paste handler (C2)
     -------------------------------------------------------- */
  const handlePaste = (e) => {
    if (disabled) return;
    e.preventDefault();

    const html = e.clipboardData.getData("text/html");
    const plain = e.clipboardData.getData("text/plain");

    /* If the clipboard contains an image file, try to insert as data URL */
    const items = e.clipboardData.items;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = String(reader.result || "");
            insertHtml(
              `<img src="${dataUrl}" alt="Pasted image" style="max-width:100%;height:auto;" />`,
            );
          };
          reader.readAsDataURL(file);
          return;
        }
      }
    }

    const cleaned = html
      ? sanitizeHtml(html)
      : String(plain || "").replace(/\n/g, "<br/>");

    document.execCommand("insertHTML", false, cleaned);
    saveSelection();
    emitChange();
  };

  /* --------------------------------------------------------
     Raw HTML mode toggle
     -------------------------------------------------------- */
  const toggleRaw = () => {
    if (!rawMode) {
      setRawText(editorRef.current?.innerHTML || body || "");
      setRawMode(true);
    } else {
      setRawMode(false);
      onChange({ subject, body: rawText || "" });
    }
  };

  const handleSubjectChange = (value) => {
    onChange({
      subject: value,
      body: rawMode ? rawText : editorRef.current?.innerHTML || "",
    });
  };

  const handleRawChange = (value) => {
    setRawText(value);
    onChange({ subject, body: value });
  };

  return (
    <div className="email-composer">
      {/* Subject — email-client framing */}
      <div className="email-composer-header">
        <div className="email-composer-row">
          <label>To:</label>
          <div className="email-composer-static">
            <strong>{"{{rep_name}}"}</strong>
            <span className="email-composer-static-hint">
              (each rep receives this in their own inbox)
            </span>
          </div>
        </div>

        <div className="email-composer-row">
          <label>From:</label>
          <div className="email-composer-static">
            <strong>IVSG CRM</strong>
            <span className="email-composer-static-hint">
              (system sender — configured in SMTP settings)
            </span>
          </div>
        </div>

        <div className="email-composer-row">
          <label>Subject:</label>
          <input
            type="text"
            className="form-control email-composer-subject"
            value={subject || ""}
            onChange={(e) => handleSubjectChange(e.target.value)}
            disabled={disabled}
            placeholder="Reminder: Next Contact for {{lead_name}}"
          />
        </div>
      </div>

      {/* Toolbar */}
      <div className="email-composer-toolbar" role="toolbar">
        {!rawMode ? (
          <>
            <div className="email-toolbar-group">
              <button
                type="button"
                title="Bold"
                onClick={() => exec("bold")}
                disabled={disabled}
              >
                <i className="fas fa-bold" />
              </button>
              <button
                type="button"
                title="Italic"
                onClick={() => exec("italic")}
                disabled={disabled}
              >
                <i className="fas fa-italic" />
              </button>
              <button
                type="button"
                title="Underline"
                onClick={() => exec("underline")}
                disabled={disabled}
              >
                <i className="fas fa-underline" />
              </button>
            </div>

            <div className="email-toolbar-divider" />

            <div className="email-toolbar-group">
              <select
                className="email-toolbar-select"
                title="Text style"
                onChange={(e) => {
                  const value = e.target.value;
                  if (value) exec("formatBlock", `<${value}>`);
                  e.target.value = "";
                }}
                disabled={disabled}
                defaultValue=""
              >
                <option value="">Paragraph</option>
                <option value="h1">Heading 1</option>
                <option value="h2">Heading 2</option>
                <option value="h3">Heading 3</option>
                <option value="p">Normal</option>
                <option value="blockquote">Quote</option>
              </select>
              <button
                type="button"
                title="Bullet list"
                onClick={() => exec("insertUnorderedList")}
                disabled={disabled}
              >
                <i className="fas fa-list-ul" />
              </button>
              <button
                type="button"
                title="Numbered list"
                onClick={() => exec("insertOrderedList")}
                disabled={disabled}
              >
                <i className="fas fa-list-ol" />
              </button>
            </div>

            <div className="email-toolbar-divider" />

            <div className="email-toolbar-group">
              <button
                type="button"
                title="Insert link"
                onClick={insertLink}
                disabled={disabled}
              >
                <i className="fas fa-link" />
              </button>
              <button
                type="button"
                title="Insert image"
                onClick={insertImage}
                disabled={disabled}
              >
                <i className="fas fa-image" />
              </button>
              <button
                type="button"
                title="Clear formatting"
                onClick={removeFormat}
                disabled={disabled}
              >
                <i className="fas fa-eraser" />
              </button>
            </div>

            <div className="email-toolbar-divider" />

            <div className="email-toolbar-group email-toolbar-group-right">
              <button
                type="button"
                title="Edit raw HTML source"
                onClick={toggleRaw}
                disabled={disabled}
              >
                <i className="fas fa-code" />
                <span className="email-toolbar-label">Source</span>
              </button>
            </div>
          </>
        ) : (
          <div className="email-toolbar-group email-toolbar-group-right">
            <button
              type="button"
              title="Back to visual editor"
              onClick={toggleRaw}
              disabled={disabled}
            >
              <i className="fas fa-eye" />
              <span className="email-toolbar-label">Visual</span>
            </button>
            <span className="email-toolbar-raw-hint">Editing raw HTML</span>
          </div>
        )}
      </div>

      {/* Body — visual canvas */}
      {!rawMode ? (
        <div
          ref={editorRef}
          className="email-composer-canvas"
          contentEditable={!disabled}
          suppressContentEditableWarning
          spellCheck
          autoCorrect="off"
          autoCapitalize="sentences"
          onInput={emitChange}
          onBlur={() => {
            saveSelection();
            emitChange();
          }}
          onKeyUp={saveSelection}
          onMouseUp={saveSelection}
          onPaste={handlePaste}
          role="textbox"
          aria-multiline="true"
          aria-label="Email body"
        />
      ) : (
        <textarea
          className="email-composer-raw-body"
          value={rawText}
          onChange={(e) => handleRawChange(e.target.value)}
          disabled={disabled}
          rows={14}
          spellCheck={false}
        />
      )}

      {/* Data-URL warning */}
      {imgWarning && (
        <div className="email-composer-warning">
          <i className="fas fa-exclamation-triangle" />
          <span>
            This email contains a pasted image (base64). For reliable delivery
            across Gmail, Outlook and mobile clients, use a hosted image URL
            (e.g. <code>https://crm.ivsgllc.com/images/logo.png</code>).
          </span>
        </div>
      )}

      {/* Placeholder chips */}
      <div className="email-composer-placeholders">
        <div className="email-composer-placeholders-label">
          Placeholders — click to insert at cursor:
        </div>
        <div className="email-composer-placeholder-chips">
          {PLACEHOLDERS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => insertPlaceholder(p)}
              disabled={disabled || rawMode}
              title="Insert at cursor"
            >
              {p}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default EmailTemplateEditor;